import type { FastifyRequest, FastifyReply } from 'fastify'
import { db } from '../core/database/firebase.js'
import { verifyAuth0Token } from '../core/auth/auth0.js'
import type { AuthUser } from '../core/auth/auth0.js'
import { isAdminRole, isString } from '../common/utils/type-guards.js'

declare module 'fastify' {
  interface FastifyRequest {
    user?: AuthUser
  }
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }
  const sanitized: string[] = []
  for (const item of value) {
    if (isString(item)) {
      const trimmed = item.trim()
      if (trimmed.length > 0) {
        sanitized.push(trimmed)
      }
    }
  }
  return sanitized
}

/**
 * SECURITY FIX (ISSUE-005): Compute effective admin status with Firestore-first precedence.
 * Firestore admin field is source of truth and can revoke admin even if Auth0 says admin.
 */
function computeEffectiveAdmin(
  firestoreAdminValue: unknown,
  authUserAdmin: boolean | undefined,
  roles: string[]
): boolean {
  // If Firestore has explicit admin flag, always use it (source of truth)
  if (typeof firestoreAdminValue === 'boolean') {
    return firestoreAdminValue
  }

  // No explicit Firestore flag: check Auth0 claim first
  if (authUserAdmin === true) {
    return true
  }

  // Finally check role-based admin
  return roles.includes('admin') || roles.includes('superadmin')
}

function mergeAuthUserWithFirestore(
  authUser: AuthUser,
  userData: Record<string, unknown> | undefined
): AuthUser {
  if (!userData) {
    return authUser
  }

  const normalizePlanTier = (value: unknown): 'free' | 'premium' | 'pro' => {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (normalized.includes('premium')) return 'premium'
      if (normalized.includes('pro')) return 'pro'
      if (normalized === 'free') return 'free'
    }
    return 'free'
  }

  const rolesValue = userData['roles']
  const roleValue = userData['role']
  const permissionsValue = userData['permissions']
  const adminValue = userData['admin']
  const subscriptionStatusValue = userData['subscriptionStatus']
  const planTierValue = userData['planTier'] ?? userData['plan']

  const extraRoles = [
    ...toStringArray(rolesValue),
    ...(typeof roleValue === 'string' && roleValue.trim().length > 0 ? [roleValue.trim()] : [])
  ]
  const mergedRoles =
    extraRoles.length > 0
      ? Array.from(new Set([...authUser.roles, ...extraRoles]))
      : authUser.roles

  const extraPermissions = toStringArray(permissionsValue)
  const mergedPermissions =
    extraPermissions.length > 0
      ? Array.from(new Set([...authUser.permissions, ...extraPermissions]))
      : authUser.permissions

  const effectiveAdmin = computeEffectiveAdmin(adminValue, authUser.admin, mergedRoles)

  const hasSuperAdminRole = mergedRoles.includes('superadmin')
  const hasModeratorRole = mergedRoles.includes('moderator')

  const planTier = normalizePlanTier(planTierValue)
  const premium = planTier === 'premium' || planTier === 'pro'

  const subscriptionStatus =
    typeof subscriptionStatusValue === 'string' && subscriptionStatusValue.trim().length > 0
      ? subscriptionStatusValue.trim()
      : undefined

  let role: AuthUser['role'] = 'user'
  if (hasSuperAdminRole) {
    role = 'superadmin'
  } else if (effectiveAdmin) {
    role = 'admin'
  } else if (hasModeratorRole) {
    role = 'moderator'
  }

  return {
    ...authUser,
    roles: mergedRoles,
    permissions: mergedPermissions,
    admin: effectiveAdmin,
    premium,
    subscriptionStatus,
    plan: planTier,
    role
  }
}

/**
 * Check if activation is required for this request.
 * Admins and superadmins bypass activation check.
 * /auth/sync and /auth/me endpoints are whitelisted to allow initial profile sync.
 */
function isActivationRequired(request: FastifyRequest, user: AuthUser): boolean {
  // Skip activation check in development/test environments
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    return false
  }

  // Admins/superadmins bypass activation
  if (user.admin || isAdminRole(user.role)) {
    return false
  }

  // Whitelist auth endpoints needed for initial sync
  const url = request.url.split('?')[0]
  const whitelistedPaths = ['/api/auth/sync', '/api/auth/me', '/api/auth/profile']
  if (whitelistedPaths.some(path => url.endsWith(path))) {
    return false
  }

  return true
}

export async function authMiddleware(
  request: FastifyRequest,
  reply: FastifyReply
): Promise<void> {
  try {
    const authHeader = request.headers.authorization

    if (!authHeader?.startsWith('Bearer ')) {
      return reply.code(401).send({ message: 'Missing or invalid authorization header' })
    }

    const token = authHeader.slice(7).trim()
    if (token.length === 0) {
      return reply.code(401).send({ message: 'Missing or invalid authorization header' })
    }

    const authUser = await verifyAuth0Token(token, request.log)
    let mergedUser = authUser
    let userActivated = true

    try {
      const userDoc = await db.collection('users').doc(authUser.uid).get()
      const userData = userDoc.data() as Record<string, unknown> | undefined
      mergedUser = mergeAuthUserWithFirestore(authUser, userData)

      // Check activation status from Firestore
      // Default to true for existing users without the field (backward compatibility)
      userActivated = userData?.activated !== false
    } catch (error) {
      request.log.warn({ err: error }, 'auth0_firestore_user_fetch_failed')
    }

    request.user = mergedUser

    // Check if user is activated (production mode only)
    if (isActivationRequired(request, mergedUser) && !userActivated) {
      request.log.info({ userId: mergedUser.uid }, 'user_not_activated')
      return reply.code(403).send({
        error: 'NOT_ACTIVATED',
        message: 'Votre compte n\'est pas encore activé. L\'accès à l\'application est restreint pendant la phase de développement.'
      })
    }
  } catch (error) {
    request.log.error(error, 'auth_middleware_error')
    return reply.code(401).send({ message: 'Invalid or expired token' })
  }
}
